import { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Swal from "sweetalert2";
import HeaderComponent from "../components/HeaderComponent";
import useTruckYardRole from "../../../hooks/useTruckYardRole";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import {
  useHikvisionAccessStore,
  fetchHikvisionDevices,
  syncCardsAndWait,
} from "../../../store/oasis/hikvisionAccessStore";
import type {
  AccessCard,
  CreateAccessCardParams,
  UpdateAccessCardParams,
  HikvisionDevice,
} from "../../../store/oasis/hikvisionAccessStore";
import {
  fetchTruckYardOptions,
  type TruckYardOption,
} from "../../../utils/truckYardOptions";
import { useTruckYardStore } from "../../../store/truckYardStore";
import { useAllTruckYardVehicles } from "../../../features/truckYard/queries";
import ExportButtons from "../../reports/components/ExportButtons";
import type { ReportTableColumn } from "../../reports/components/ReportPointsTable";
import {
  NO_YARD_FILTER,
  cardYardNames,
  matchesYardFilter,
  resolveCardYardIds,
} from "./cardYards";
import { isCardListTruncated } from "./cardListTruncation";
import { escapeHtml } from "../../../utils/escapeHtml";

// Las opciones de los selects de esta página (patio, equipo, vehículo).
type SelectOption = { value: string; label: string };

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

// ─── Export rows ───────────────────────────────────────────────────────────────
// Flattened, display-ready shape for the Excel/PDF export (reuses the reports'
// exportHistoric utility).

interface CardExportRow {
  id: string;
  card_number: string;
  label: string;
  type: string;
  assigned: string;
  yards: string;
  status: string;
  validity: string;
}

const EXPORT_COLUMNS: ReportTableColumn<CardExportRow>[] = [
  { key: "card_number", header: "Nº Tarjeta" },
  { key: "label", header: "Etiqueta" },
  { key: "type", header: "Tipo" },
  { key: "assigned", header: "Asignado a" },
  { key: "yards", header: "Patio(s)" },
  { key: "status", header: "Estado" },
  { key: "validity", header: "Válida desde/hasta" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusLabel(s: AccessCard["status"], t: TFunction): string {
  switch (s) {
    case "active":
      return t("accessCards.status.active");
    case "inactive":
      return t("accessCards.status.inactive");
    case "lost":
      return t("accessCards.status.lost");
    case "expired":
      return t("accessCards.status.expired");
    default:
      return s;
  }
}

function statusClasses(s: AccessCard["status"]): string {
  switch (s) {
    case "active":
      return "bg-green-500/15 text-green-400";
    case "inactive":
      return "bg-gray-500/15 text-gray-400";
    case "lost":
      return "bg-red-500/15 text-red-400";
    case "expired":
      return "bg-orange-500/15 text-orange-400";
    default:
      return "bg-gray-500/15 text-gray-400";
  }
}

function typeLabel(cardType: AccessCard["type"], t: TFunction): string {
  return cardType === "card" ? t("accessCards.type.card") : t("accessCards.type.tag");
}

function typeIcon(t: AccessCard["type"]): string {
  return t === "card" ? "ri-bank-card-line" : "ri-price-tag-3-line";
}

function typeClasses(t: AccessCard["type"]): string {
  return t === "card"
    ? "bg-blue-500/15 text-blue-400"
    : "bg-purple-500/15 text-purple-400";
}

function typeIconBg(t: AccessCard["type"]): string {
  return t === "card"
    ? "bg-blue-500/20 text-blue-400"
    : "bg-purple-500/20 text-purple-400";
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AccessCardsSection() {
  const { t } = useTranslation("truckYard");
  const { isDarkMode } = useDarkMode();
  const { isGuard } = useTruckYardRole();
  const disableCRUD = isGuard;

  const {
    accessCards,
    cardsLoading,
    accessCardsPagination,
    fetchCards,
    createCard,
    updateCard,
    removeCard,
  } = useHikvisionAccessStore();

  const vehicles = useTruckYardStore((s) => s.vehicles);
  useAllTruckYardVehicles();

  // ─── Theme ───────────────────────────────────────────────────────────────
  const themeClasses: ThemeClasses = isDarkMode
    ? {
        bg: "bg-[#1a1a1a]",
        border: "border-[#333]",
        text: "text-white",
        textSecondary: "text-gray-400",
        cardBg: "bg-[#242424]",
        cardBorder: "border-[#333]",
        buttonHover: "hover:bg-[#2a2a2a]",
        buttonText: "text-gray-400",
        buttonTextHover: "hover:text-white",
      }
    : {
        bg: "bg-white",
        border: "border-gray-200",
        text: "text-gray-900",
        textSecondary: "text-gray-500",
        cardBg: "bg-gray-50",
        cardBorder: "border-gray-200",
        buttonHover: "hover:bg-gray-100",
        buttonText: "text-gray-500",
        buttonTextHover: "hover:text-gray-700",
      };

  const surface = isDarkMode
    ? "bg-[#2a2a2a] border-[#444] text-white"
    : "bg-gray-50 border-gray-200 text-gray-900";

  // ─── State ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState<"" | "card" | "tag">("");
  // Selección múltiple: [] = todos, NO_YARD_FILTER = sin patio, el resto son
  // yardIds. Una tarjeta pasa si CUALQUIERA de sus patios está elegido.
  const [selectedYards, setSelectedYards] = useState<string[]>([]);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editCard, setEditCard] = useState<AccessCard | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formCardNumber, setFormCardNumber] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formType, setFormType] = useState<"card" | "tag">("card");
  const [formStatus, setFormStatus] = useState<AccessCard["status"]>("active");
  const [formYardIds, setFormYardIds] = useState<string[]>([]);
  const [formAssignMode, setFormAssignMode] = useState<"vehicle" | "person">("vehicle");
  const [formVehicleId, setFormVehicleId] = useState("");
  const [formDriverName, setFormDriverName] = useState("");
  const [formDriverId, setFormDriverId] = useState("");
  const [formValidFrom, setFormValidFrom] = useState("");
  const [formValidUntil, setFormValidUntil] = useState("");

  // Yards from utility
  const [yards, setYards] = useState<TruckYardOption[]>([]);

  // ─── Import-from-controller state ──────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importYardId, setImportYardId] = useState("");
  const [importDevices, setImportDevices] = useState<HikvisionDevice[]>([]);
  const [importDevicesLoading, setImportDevicesLoading] = useState(false);
  const [importDeviceId, setImportDeviceId] = useState("");
  const [importing, setImporting] = useState(false);

  const openImport = () => {
    setImportYardId("");
    setImportDevices([]);
    setImportDeviceId("");
    setImportOpen(true);
  };

  // When a yard is picked, load its controllers.
  useEffect(() => {
    if (!importOpen || !importYardId) {
      setImportDevices([]);
      setImportDeviceId("");
      return;
    }
    let cancelled = false;
    setImportDevicesLoading(true);
    fetchHikvisionDevices({ yard_id: importYardId, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        const list: HikvisionDevice[] = Array.isArray(res?.data) ? res.data : [];
        // One device per door_no — dedupe by serial/ip so the user picks a panel,
        // not a door (a card import covers the whole controller).
        const seen = new Set<string>();
        const panels = list.filter((d) => {
          const key = d.device_serial || d.ip_address || d.id;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setImportDevices(panels);
        if (panels.length === 1) setImportDeviceId(panels[0].id);
      })
      .finally(() => !cancelled && setImportDevicesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [importOpen, importYardId]);

  const runImport = async () => {
    if (!importDeviceId) return;
    setImporting(true);
    try {
      const result = await syncCardsAndWait({ device_id: importDeviceId });
      if (result.status === "completed") {
        await fetchCards({ limit: 100 });
        setImportOpen(false);
        Swal.fire({
          icon: "success",
          title: "Tarjetas importadas",
          html: `Importadas: <b>${result.imported ?? 0}</b><br/>Ya existentes (omitidas): <b>${result.skipped ?? 0}</b>${result.errors ? `<br/>Errores: <b>${escapeHtml(String(result.errors))}</b>` : ""}`,
          confirmButtonColor: brand.primary,
        });
      } else if (result.status === "failed") {
        Swal.fire({
          icon: "error",
          title: "No se pudieron leer las tarjetas",
          text: result.error || "El controlador no respondió.",
          confirmButtonColor: brand.primary,
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "La importación sigue en proceso",
          text: "El agente en sitio (relay) no respondió a tiempo. Verifica que el relay y el HikSdkBridge estén corriendo, e inténtalo de nuevo.",
          confirmButtonColor: brand.primary,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err instanceof Error ? err.message : String(err),
        confirmButtonColor: brand.primary,
      });
    } finally {
      setImporting(false);
    }
  };

  // ─── Data fetching ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchCards({ limit: 100 });
    fetchTruckYardOptions().then(setYards);
  }, [fetchCards]);

  const yardNameById = useMemo(() => {
    const map: Record<string, string> = {};
    yards.forEach((y) => (map[y.id] = y.name));
    return map;
  }, [yards]);

  // "Patio A, Patio B" o "Sin patio": lo mismo en la card, la lista y el export.
  const yardsText = useCallback(
    (card: AccessCard): string => {
      const names = cardYardNames(card, yardNameById);
      return names.length ? names.join(", ") : t("accessCards.noYard");
    },
    [yardNameById, t],
  );

  // ─── Filtering ───────────────────────────────────────────────────────────
  const filteredCards = useMemo(() => {
    return accessCards.filter((c) => {
      const q = searchQuery.toLowerCase();
      const matchSearch =
        !searchQuery ||
        c.card_number.toLowerCase().includes(q) ||
        (c.label && c.label.toLowerCase().includes(q)) ||
        (c.vehicle_plate && c.vehicle_plate.toLowerCase().includes(q)) ||
        (c.driver_name && c.driver_name.toLowerCase().includes(q));
      const matchStatus =
        !selectedStatus || selectedStatus === "all" || c.status === selectedStatus;
      const matchType = !selectedType || c.type === selectedType;
      const matchYard = matchesYardFilter(c, selectedYards);
      return matchSearch && matchStatus && matchType && matchYard;
    });
  }, [accessCards, searchQuery, selectedStatus, selectedType, selectedYards]);

  // Ver cardListTruncation.ts: con más de 100 tarjetas el filtro y el export
  // sólo cubren las más nuevas, y el operador tiene que saberlo.
  const listTruncated = isCardListTruncated(
    accessCardsPagination?.total,
    accessCards.length,
  );

  const totalPages = Math.max(1, Math.ceil(filteredCards.length / PAGE_SIZE));
  const paginatedCards = filteredCards.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Export rows: the whole filtered set (not just the visible page), formatted
  // like the on-screen cards/list.
  const exportRows = useMemo<CardExportRow[]>(
    () =>
      filteredCards.map((c) => ({
        id: c.id,
        card_number: c.card_number,
        label: c.label || "—",
        type: typeLabel(c.type, t),
        assigned:
          c.vehicle_plate ||
          c.driver_name ||
          t("accessCards.card.unassigned"),
        yards: yardsText(c),
        status: statusLabel(c.status, t),
        validity:
          c.valid_from || c.valid_until
            ? `${c.valid_from || "..."} - ${c.valid_until || "..."}`
            : "—",
      })),
    [filteredCards, t, yardsText],
  );

  // ─── Form helpers ────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormCardNumber("");
    setFormLabel("");
    setFormType("card");
    setFormStatus("active");
    setFormYardIds([]);
    setFormAssignMode("vehicle");
    setFormVehicleId("");
    setFormDriverName("");
    setFormDriverId("");
    setFormValidFrom("");
    setFormValidUntil("");
  };

  const openAdd = () => {
    setEditCard(null);
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (card: AccessCard) => {
    setEditCard(card);
    setFormCardNumber(card.card_number);
    setFormLabel(card.label || "");
    setFormType(card.type || "card");
    setFormStatus(card.status);
    setFormYardIds(resolveCardYardIds(card));
    setFormAssignMode(card.driver_id || card.driver_name ? "person" : "vehicle");
    setFormVehicleId(card.vehicle_id || "");
    setFormDriverName(card.driver_name || "");
    setFormDriverId(card.driver_id || "");
    setFormValidFrom(card.valid_from || "");
    setFormValidUntil(card.valid_until || "");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditCard(null);
  };

  // Build vehicle options for Select
  const vehicleOptions = useMemo(() => {
    return vehicles.map((v) => ({
      value: v.id,
      label: `${v.name || t("accessCards.form.noName")} | ${v.plate || t("accessCards.form.noPlate")}`,
    }));
  }, [vehicles, t]);

  // Build yard options for Select
  const yardOptions = useMemo(() => {
    return yards.map((y) => ({
      value: y.id,
      label: y.name,
    }));
  }, [yards]);

  // A card can be assigned to multiple patios, just like a vehicle. In
  // "vehicle" mode those patios are inherited from the assigned vehicle's
  // yard_ids and shown as read-only pills; in "person" mode they're chosen
  // freely from a multi-select.
  const assignedVehicle = useMemo(
    () => vehicles.find((v) => v.id === formVehicleId) || null,
    [vehicles, formVehicleId],
  );

  const inheritedYardIds = useMemo(
    () =>
      formAssignMode === "vehicle" ? assignedVehicle?.yard_ids || [] : [],
    [formAssignMode, assignedVehicle],
  );

  // In vehicle mode keep the card's patios identical to the assigned vehicle's.
  useEffect(() => {
    if (formAssignMode !== "vehicle") return;
    setFormYardIds((prev) => {
      const same =
        prev.length === inheritedYardIds.length &&
        inheritedYardIds.every((id) => prev.includes(id));
      return same ? prev : [...inheritedYardIds];
    });
  }, [formAssignMode, inheritedYardIds]);

  const selectedYardOptions = useMemo(
    () => yardOptions.filter((o) => formYardIds.includes(o.value)),
    [yardOptions, formYardIds],
  );

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formCardNumber.trim() || !formLabel.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("accessCards.messages.requiredTitle"),
        text: t("accessCards.messages.requiredText"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    setIsSubmitting(true);

    const selectedVehicle = vehicles.find((v) => v.id === formVehicleId);
    const vehiclePlate = selectedVehicle?.plate
      || (selectedVehicle?.name?.includes(" | ") ? selectedVehicle.name.split(" | ").pop() : null)
      || null;

    const isVehicle = formAssignMode === "vehicle";

    try {
      if (editCard) {
        const payload: UpdateAccessCardParams = {
          id: editCard.id,
          card_number: formCardNumber.trim(),
          label: formLabel.trim(),
          type: formType,
          status: formStatus,
          yard_ids: formYardIds,
          // Keep the legacy single field populated for backward compatibility.
          yard_id: formYardIds[0] || null,
          vehicle_id: isVehicle ? (formVehicleId || null) : null,
          vehicle_plate: isVehicle ? vehiclePlate : null,
          driver_id: isVehicle ? null : (formDriverId || null),
          driver_name: isVehicle ? null : (formDriverName || null),
          valid_from: formValidFrom || null,
          valid_until: formValidUntil || null,
        };
        const result = await updateCard(payload);
        if (result) {
          Swal.fire({
            icon: "success",
            title: t("accessCards.messages.updatedTitle"),
            confirmButtonColor: brand.primary,
          });
          closeForm();
          await fetchCards({ limit: 100 });
        } else {
          Swal.fire({
            icon: "error",
            title: t("common.error"),
            text: t("accessCards.messages.updateErrorText"),
            confirmButtonColor: "#ef4444",
          });
        }
      } else {
        const payload: CreateAccessCardParams = {
          card_number: formCardNumber.trim(),
          label: formLabel.trim(),
          type: formType,
          status: formStatus,
          yard_ids: formYardIds,
          // Keep the legacy single field populated for backward compatibility.
          yard_id: formYardIds[0] || null,
          vehicle_id: isVehicle ? (formVehicleId || null) : null,
          vehicle_plate: isVehicle ? vehiclePlate : null,
          driver_id: isVehicle ? null : (formDriverId || null),
          driver_name: isVehicle ? null : (formDriverName || null),
          valid_from: formValidFrom || null,
          valid_until: formValidUntil || null,
        };
        const result = await createCard(payload);
        if (result) {
          Swal.fire({
            icon: "success",
            title: t("accessCards.messages.createdTitle"),
            confirmButtonColor: brand.primary,
          });
          closeForm();
          await fetchCards({ limit: 100 });
        } else {
          Swal.fire({
            icon: "error",
            title: t("common.error"),
            text: t("accessCards.messages.createErrorText"),
            confirmButtonColor: "#ef4444",
          });
        }
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: t("common.error"),
        text: t("accessCards.messages.saveErrorText"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────
  const handleDelete = async (card: AccessCard) => {
    const result = await Swal.fire({
      title: t("accessCards.messages.deleteTitle"),
      text: t("accessCards.messages.deleteText", {
        number: card.card_number,
        label: card.label ? ` (${card.label})` : "",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: brand.primary,
      confirmButtonText: t("accessCards.messages.deleteConfirm"),
      cancelButtonText: t("common.cancel"),
    });
    if (result.isConfirmed) {
      const success = await removeCard(card.id);
      if (success) {
        Swal.fire({
          title: t("accessCards.messages.deletedTitle"),
          icon: "success",
          confirmButtonColor: brand.primary,
        });
        await fetchCards({ limit: 100 });
      } else {
        Swal.fire({
          title: t("common.error"),
          text: t("accessCards.messages.deleteErrorText"),
          icon: "error",
          confirmButtonColor: "#ef4444",
        });
      }
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col relative">
      {/* Header */}
      <HeaderComponent
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        title={t("accessCards.title")}
        subtitle={t("accessCards.subtitle")}
        searchQuery={searchQuery}
        selectedStatus={selectedStatus}
        selectedView={selectedView}
        onSearchChange={(v: string) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        onStatusChange={(v: string) => {
          setSelectedStatus(v);
          setCurrentPage(1);
        }}
        onViewChange={setSelectedView}
        onAdd={openAdd}
        addButtonText={t("accessCards.addButton")}
        addButtonShortText={t("accessCards.addButtonShort")}
        statusOptions={[
          { value: "all", label: t("accessCards.status.all") },
          { value: "active", label: t("accessCards.status.active") },
          { value: "inactive", label: t("accessCards.status.inactive") },
          { value: "lost", label: t("accessCards.status.lost") },
          { value: "expired", label: t("accessCards.status.expired") },
        ]}
        searchPlaceholder={t("accessCards.searchPlaceholder")}
        extraFilters={[
          {
            value: selectedType,
            onChange: (v: string) => {
              setSelectedType(v as "" | "card" | "tag");
              setCurrentPage(1);
            },
            options: [
              { value: "", label: t("accessCards.allTypes") },
              { value: "card", label: t("accessCards.type.card") },
              { value: "tag", label: t("accessCards.type.tag") },
            ],
            placeholder: t("accessCards.typeFilterPlaceholder"),
          },
          {
            multi: true,
            values: selectedYards,
            onChange: (v: string[]) => {
              setSelectedYards(v);
              setCurrentPage(1);
            },
            options: [
              ...yardOptions,
              { value: NO_YARD_FILTER, label: t("accessCards.noYard") },
            ],
            placeholder: t("accessCards.allYards"),
            noOptionsMessage: t("accessCards.noYardOptions"),
          },
        ]}
        disableCRUD={disableCRUD}
      />

      {/* Toolbar: export (all roles) + import from controller (non-guards) */}
      <div className="px-4 sm:px-6 pt-3 -mb-1 flex flex-wrap justify-end gap-2">
        <ExportButtons
          rows={exportRows}
          columns={EXPORT_COLUMNS}
          meta={{
            title: t("accessCards.title"),
            period: `${filteredCards.length} tarjeta(s)`,
            fileBase: "tarjetas_acceso",
          }}
        />
        {!disableCRUD && (
          <button
            type="button"
            onClick={openImport}
            className="inline-flex items-center gap-2 rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-1.5 text-sm font-medium text-purple-400 hover:bg-purple-500/20 transition-colors"
            title="Copiar todas las tarjetas creadas en el controlador a esta lista"
          >
            <i className="ri-download-cloud-2-line" />
            Importar del controlador
          </button>
        )}
      </div>

      {listTruncated && (
        <div className="px-4 sm:px-6 pt-3">
          <div
            role="status"
            className={`flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm ${isDarkMode ? "text-amber-400" : "text-amber-700"}`}
          >
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
            <span>
              {t("accessCards.truncatedNotice", {
                shown: accessCards.length,
                total: accessCardsPagination?.total,
              })}
            </span>
          </div>
        </div>
      )}

      {/* Import modal */}
      {importOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !importing && setImportOpen(false)}
        >
          <div
            className={`w-full max-w-md rounded-xl border ${themeClasses.border} ${themeClasses.bg} p-5 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={`text-lg font-semibold ${themeClasses.text}`}>
              Importar tarjetas del controlador
            </h3>
            <p className={`mt-1 text-sm ${themeClasses.textSecondary}`}>
              Copia todas las tarjetas creadas en el controlador a esta lista. Las
              tarjetas que ya existen no se modifican.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                  Patio
                </label>
                <Select
                  options={yardOptions}
                  value={yardOptions.find((o) => o.value === importYardId) || null}
                  onChange={(o) => setImportYardId(o?.value || "")}
                  styles={getCustomSelectStyles<SelectOption, false>(isDarkMode)}
                  placeholder="Selecciona un patio"
                  isDisabled={importing}
                />
              </div>
              <div>
                <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                  Controlador
                </label>
                <Select
                  options={importDevices.map((d) => ({
                    value: d.id,
                    label: `${d.device_name || d.device_serial || d.ip_address || d.id}${d.device_serial ? ` (${d.device_serial})` : ""}`,
                  }))}
                  value={
                    importDevices
                      .map((d) => ({
                        value: d.id,
                        label: `${d.device_name || d.device_serial || d.ip_address || d.id}${d.device_serial ? ` (${d.device_serial})` : ""}`,
                      }))
                      .find((o) => o.value === importDeviceId) || null
                  }
                  onChange={(o) => setImportDeviceId(o?.value || "")}
                  styles={getCustomSelectStyles<SelectOption, false>(isDarkMode)}
                  placeholder={
                    importDevicesLoading
                      ? "Cargando…"
                      : importYardId
                        ? "Selecciona un controlador"
                        : "Primero elige un patio"
                  }
                  isDisabled={importing || !importYardId || importDevicesLoading}
                  noOptionsMessage={() =>
                    importYardId ? "Sin controladores en este patio" : "Elige un patio"
                  }
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={importing}
                onClick={() => setImportOpen(false)}
                className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.text} ${themeClasses.buttonHover} disabled:opacity-50`}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={importing || !importDeviceId}
                onClick={runImport}
                className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: brand.primary }}
              >
                {importing ? "Importando…" : "Importar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {cardsLoading ? (
          <div
            className={`py-16 text-center text-sm ${themeClasses.textSecondary}`}
          >
            {t("accessCards.loading")}
          </div>
        ) : paginatedCards.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16">
            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${themeClasses.cardBg}`}
            >
              <i
                className={`ri-bank-card-line text-4xl ${themeClasses.textSecondary}`}
              ></i>
            </div>
            <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
              {t("accessCards.empty.title")}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary} mb-4`}>
              {t("accessCards.empty.subtitle")}
            </p>
            {!disableCRUD && (
              <button
                onClick={openAdd}
                className="px-4 py-2 rounded-lg font-medium text-sm"
                style={{ backgroundColor: brand.primary, color: brand.accent }}
              >
                <i className="ri-add-line mr-1"></i>
                {t("accessCards.addButton")}
              </button>
            )}
          </div>
        ) : selectedView === "grid" ? (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {paginatedCards.map((card) => (
              <div
                key={card.id}
                className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all duration-200`}
              >
                {/* Top: icon + card number + status */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${typeIconBg(card.type)}`}
                    >
                      <i className={`${typeIcon(card.type)} text-xl`}></i>
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`font-semibold text-sm font-mono ${themeClasses.text} truncate`}
                      >
                        {card.card_number}
                      </p>
                      <p
                        className={`text-xs ${themeClasses.textSecondary} truncate`}
                      >
                        {card.label || t("accessCards.card.noLabel")}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusClasses(card.status)}`}
                  >
                    {statusLabel(card.status, t)}
                  </span>
                </div>

                {/* Info block */}
                <div
                  className={`${isDarkMode ? "bg-[#252525]" : "bg-gray-50"} rounded-xl p-3 space-y-2 mb-4`}
                >
                  {/* Type badge */}
                  <div className="flex items-center gap-2">
                    <i
                      className={`${typeIcon(card.type)} text-xs ${themeClasses.textSecondary}`}
                    ></i>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeClasses(card.type)}`}
                    >
                      {typeLabel(card.type, t)}
                    </span>
                  </div>
                  {/* Assignment */}
                  <div className="flex items-center gap-2">
                    {card.vehicle_plate ? (
                      <>
                        <i
                          className={`ri-truck-line text-xs ${themeClasses.textSecondary}`}
                        ></i>
                        <span
                          className={`text-xs ${themeClasses.textSecondary} truncate`}
                        >
                          {card.vehicle_plate}
                        </span>
                      </>
                    ) : card.driver_name ? (
                      <>
                        <i
                          className={`ri-user-line text-xs ${themeClasses.textSecondary}`}
                        ></i>
                        <span
                          className={`text-xs ${themeClasses.textSecondary} truncate`}
                        >
                          {card.driver_name}
                        </span>
                      </>
                    ) : (
                      <>
                        <i
                          className={`ri-link-unlink text-xs ${themeClasses.textSecondary}`}
                        ></i>
                        <span
                          className={`text-xs ${themeClasses.textSecondary}`}
                        >
                          {t("accessCards.card.unassigned")}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Yards */}
                  <div className="flex items-center gap-2 min-w-0">
                    <i
                      className={`ri-building-4-line text-xs ${themeClasses.textSecondary}`}
                    ></i>
                    <span
                      className={`text-xs ${themeClasses.textSecondary} truncate`}
                      title={yardsText(card)}
                    >
                      {yardsText(card)}
                    </span>
                  </div>
                  {/* Validity */}
                  {(card.valid_from || card.valid_until) && (
                    <div className="flex items-center gap-2">
                      <i
                        className={`ri-calendar-line text-xs ${themeClasses.textSecondary}`}
                      ></i>
                      <span
                        className={`text-xs ${themeClasses.textSecondary} truncate`}
                      >
                        {card.valid_from || "..."} - {card.valid_until || "..."}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!disableCRUD && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(card)}
                      className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                      title={t("common.edit")}
                    >
                      <i
                        className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
                      ></i>
                    </button>
                    <button
                      onClick={() => handleDelete(card)}
                      className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
                      title={t("common.delete")}
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {paginatedCards.map((card) => (
              <div
                key={card.id}
                className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
              >
                {/* Mobile */}
                <div className="flex items-center gap-3 sm:hidden">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeIconBg(card.type)}`}
                  >
                    <i className={`${typeIcon(card.type)} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-semibold text-sm font-mono ${themeClasses.text} truncate`}
                    >
                      {card.card_number}
                    </p>
                    <p
                      className={`text-xs ${themeClasses.textSecondary} truncate`}
                    >
                      {card.label || t("accessCards.card.noLabel")}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClasses(card.status)}`}
                  >
                    {statusLabel(card.status, t)}
                  </span>
                </div>

                {/* Desktop */}
                <div className="hidden sm:flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeIconBg(card.type)}`}
                    >
                      <i className={`${typeIcon(card.type)} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`font-semibold text-sm font-mono ${themeClasses.text} truncate`}
                      >
                        {card.card_number}
                      </p>
                      <span
                        className={`text-xs ${themeClasses.textSecondary}`}
                      >
                        {card.label || t("accessCards.card.noLabel")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 flex-shrink-0">
                    {/* Type */}
                    <div className="hidden md:block text-center min-w-[80px]">
                      <p
                        className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
                      >
                        {t("accessCards.list.type")}
                      </p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeClasses(card.type)}`}
                      >
                        {typeLabel(card.type, t)}
                      </span>
                    </div>

                    {/* Assignment */}
                    <div className="hidden lg:block text-center min-w-[140px]">
                      <p
                        className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
                      >
                        {t("accessCards.list.assignedTo")}
                      </p>
                      <p className={`text-sm ${themeClasses.text} truncate`}>
                        {card.vehicle_plate ||
                          card.driver_name ||
                          t("accessCards.card.unassigned")}
                      </p>
                    </div>

                    {/* Yards */}
                    <div className="hidden lg:block text-center min-w-[140px] max-w-[200px]">
                      <p
                        className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}
                      >
                        {t("accessCards.list.yards")}
                      </p>
                      <p
                        className={`text-sm ${themeClasses.text} truncate`}
                        title={yardsText(card)}
                      >
                        {yardsText(card)}
                      </p>
                    </div>

                    {/* Status */}
                    <span
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusClasses(card.status)}`}
                    >
                      {statusLabel(card.status, t)}
                    </span>

                    {/* Actions */}
                    {!disableCRUD && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(card)}
                          className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
                          title={t("common.edit")}
                        >
                          <i
                            className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
                          ></i>
                        </button>
                        <button
                          onClick={() => handleDelete(card)}
                          className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
                          title={t("common.delete")}
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${themeClasses.cardBorder} ${themeClasses.buttonHover} ${themeClasses.buttonText} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {t("common.previous")}
            </button>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {t("common.page", { current: currentPage, total: totalPages })}
            </span>
            <button
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${themeClasses.cardBorder} ${themeClasses.buttonHover} ${themeClasses.buttonText} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>

      {/* ─── Form Modal ───────────────────────────────────────────────────── */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeForm}
          />
          <div
            className={`relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl ${themeClasses.cardBg} ${themeClasses.cardBorder} border p-6`}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-base font-bold ${themeClasses.text}`}>
                {editCard ? t("accessCards.form.editTitle") : t("accessCards.form.newTitle")}
              </h2>
              <button
                onClick={closeForm}
                className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              >
                <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Type toggle */}
              <div>
                <label
                  className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                >
                  {t("accessCards.form.type")}
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormType("card")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formType === "card"
                        ? "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40"
                        : `${themeClasses.cardBg} ${themeClasses.textSecondary}`
                    }`}
                  >
                    <i className="ri-bank-card-line mr-1.5" />
                    {t("accessCards.type.card")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormType("tag")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      formType === "tag"
                        ? "bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/40"
                        : `${themeClasses.cardBg} ${themeClasses.textSecondary}`
                    }`}
                  >
                    <i className="ri-price-tag-3-line mr-1.5" />
                    {t("accessCards.type.tag")}
                  </button>
                </div>
              </div>

              {/* Card number + Label */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                  >
                    {t("accessCards.form.cardNumber")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formCardNumber}
                    onChange={(e) => setFormCardNumber(e.target.value)}
                    placeholder={t("accessCards.form.cardNumberPlaceholder")}
                    className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                  />
                </div>
                <div>
                  <label
                    className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                  >
                    {t("accessCards.form.label")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formLabel}
                    onChange={(e) => setFormLabel(e.target.value)}
                    placeholder={t("accessCards.form.labelPlaceholder")}
                    className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label
                  className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                >
                  {t("accessCards.form.status")}
                </label>
                <select
                  value={formStatus}
                  onChange={(e) =>
                    setFormStatus(e.target.value as AccessCard["status"])
                  }
                  className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                >
                  <option value="active">{t("accessCards.status.active")}</option>
                  <option value="inactive">{t("accessCards.status.inactive")}</option>
                  <option value="lost">{t("accessCards.status.lost")}</option>
                  <option value="expired">{t("accessCards.status.expired")}</option>
                </select>
              </div>

              {/* Assignment toggle */}
              <div>
                <p
                  className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-3`}
                >
                  {t("accessCards.form.assignTo")}
                </p>
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFormAssignMode("vehicle");
                      setFormDriverName("");
                      setFormDriverId("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formAssignMode === "vehicle" ? "bg-blue-500/20 text-blue-400 ring-1 ring-blue-500/40" : `${themeClasses.cardBg} ${themeClasses.textSecondary}`}`}
                  >
                    <i className="ri-truck-line mr-1.5" />
                    {t("accessCards.form.vehicle")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormAssignMode("person");
                      setFormVehicleId("");
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formAssignMode === "person" ? "bg-teal-500/20 text-teal-400 ring-1 ring-teal-500/40" : `${themeClasses.cardBg} ${themeClasses.textSecondary}`}`}
                  >
                    <i className="ri-user-line mr-1.5" />
                    {t("accessCards.form.person")}
                  </button>
                </div>

                {formAssignMode === "vehicle" ? (
                  <Select
                    options={vehicleOptions}
                    value={
                      vehicleOptions.find((o) => o.value === formVehicleId) ||
                      null
                    }
                    onChange={(opt) => setFormVehicleId(opt?.value || "")}
                    isClearable
                    placeholder={t("accessCards.form.selectVehicle")}
                    styles={getCustomSelectStyles<SelectOption, false>(isDarkMode)}
                    noOptionsMessage={() => t("accessCards.form.noVehicles")}
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                      >
                        {t("accessCards.form.name")}
                      </label>
                      <input
                        type="text"
                        value={formDriverName}
                        onChange={(e) => setFormDriverName(e.target.value)}
                        placeholder={t("accessCards.form.namePlaceholder")}
                        className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                      />
                    </div>
                    <div>
                      <label
                        className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                      >
                        {t("accessCards.form.identifier")}
                      </label>
                      <input
                        type="text"
                        value={formDriverId}
                        onChange={(e) => setFormDriverId(e.target.value)}
                        placeholder={t("accessCards.form.identifierPlaceholder")}
                        className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Validity dates */}
              <div>
                <p
                  className={`text-[10px] uppercase tracking-widest font-semibold ${themeClasses.textSecondary} mb-3`}
                >
                  {t("accessCards.form.validity")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                    >
                      {t("accessCards.form.validFrom")}
                    </label>
                    <input
                      type="date"
                      value={formValidFrom}
                      onChange={(e) => setFormValidFrom(e.target.value)}
                      className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                    />
                  </div>
                  <div>
                    <label
                      className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                    >
                      {t("accessCards.form.validUntil")}
                    </label>
                    <input
                      type="date"
                      value={formValidUntil}
                      onChange={(e) => setFormValidUntil(e.target.value)}
                      className={`w-full px-3 py-2.5 ${surface} border rounded-xl outline-none transition-all text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50`}
                    />
                  </div>
                </div>
              </div>

              {/* Yards — inherited from the assigned vehicle, shown last */}
              <div>
                <label
                  className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`}
                >
                  {t("accessCards.form.yards")}
                </label>
                {formAssignMode === "vehicle" ? (
                  /* Inherited from the assigned vehicle — shown as read-only pills. */
                  <div
                    className={`w-full min-h-[44px] px-3 py-2.5 ${surface} border rounded-xl text-sm flex flex-wrap gap-1.5 items-center`}
                  >
                    {formYardIds.length === 0 ? (
                      <span className={`text-xs italic ${themeClasses.textSecondary}`}>
                        {formVehicleId
                          ? t("accessCards.form.vehicleNoYards")
                          : t("accessCards.form.selectVehicleToInherit")}
                      </span>
                    ) : (
                      formYardIds.map((id) => (
                        <span
                          key={id}
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${
                            isDarkMode
                              ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                              : "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}
                        >
                          <i className="ri-building-4-line mr-1 text-[10px]"></i>
                          {yardNameById[id] || id}
                        </span>
                      ))
                    )}
                  </div>
                ) : (
                  <Select
                    isMulti
                    options={yardOptions}
                    value={selectedYardOptions}
                    onChange={(opts) =>
                      setFormYardIds((opts || []).map((o) => o.value))
                    }
                    placeholder={t("accessCards.form.selectYards")}
                    styles={getCustomSelectStyles<SelectOption, true>(isDarkMode)}
                    noOptionsMessage={() => t("accessCards.form.noYards")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                {formAssignMode === "vehicle" && (
                  <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
                    {t("accessCards.form.yardsInherited")}
                  </p>
                )}
              </div>

              {/* Submit buttons */}
              <div
                className={`flex justify-end gap-3 pt-4 border-t ${themeClasses.cardBorder}`}
              >
                <button
                  type="button"
                  onClick={closeForm}
                  className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: brand.primary,
                    color: brand.accent,
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {editCard ? t("accessCards.form.updating") : t("accessCards.form.creating")}
                    </>
                  ) : editCard ? (
                    t("accessCards.form.update")
                  ) : (
                    t("accessCards.form.create")
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
